import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  creatorIdsForHandle,
  extractMentionHandles,
  mergeCreatorIds,
  stripMentions,
} from "../lib/creatorScope";
import { searchCreators } from "../lib/creatorDirectory";
import CreatorAvatar from "./CreatorAvatar";

const MENTION_TOKEN_PATTERN =
  /(?:^|[^A-Za-z0-9_@./-])@([A-Za-z0-9][A-Za-z0-9_.]*)?$/;
const MAX_SUGGESTIONS = 6;

/**
 * The `@handle` being typed at the caret, or null. Only a token the caret sits
 * at the end of counts, so editing earlier text never reopens the menu.
 */
function readMentionToken(textarea) {
  if (!textarea) return null;
  const { selectionStart, selectionEnd, value } = textarea;
  if (selectionStart !== selectionEnd) return null;

  const before = value.slice(0, selectionStart);
  const match = before.match(MENTION_TOKEN_PATTERN);
  if (!match) return null;

  const query = match[1] || "";
  return {
    query,
    start: selectionStart - query.length - 1,
    end: selectionStart,
  };
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * The directory and the creator corpus identify people differently: the
 * directory has a chosen code (`healthyvoyager`), the corpus indexes the
 * creator's name (`carolynscott_platform`). Mentions have to name the corpus,
 * so a suggestion inserts the name-derived handle and keeps the code only for
 * matching what the user types.
 */
function normalizeCreator(raw) {
  const code = String(raw?.code || "").trim();
  const first = String(raw?.first_name || "").trim();
  const last = String(raw?.last_name || "").trim();
  const name = [first, last].filter(Boolean).join(" ");
  // Creators already seen in the thread arrive with the corpus handle itself,
  // which beats any guess made from a name.
  const handle =
    String(raw?.handle || "").trim() || slugify(name) || slugify(code);
  if (!handle) return null;

  return {
    handle,
    code,
    name: name || code || handle,
    logo: typeof raw?.logo === "string" ? raw.logo : "",
  };
}

function dedupeByHandle(creators) {
  const seen = new Set();
  const list = [];

  for (const creator of creators) {
    if (!creator?.handle) continue;
    if (seen.has(creator.handle)) continue;
    seen.add(creator.handle);
    list.push(creator);
  }

  return list;
}

export default function ChatInput({
  placeholder = "Ask anything",
  autoFocus = false,
  focusSignal = 0,
  disabled = false,
  onSubmit,
  // Creators this conversation has already surfaced. They lead the
  // suggestions: the ones on screen are the ones most likely meant.
  knownCreators = [],
  // Bump `nonce` to drop a mention in from outside (a creator badge click).
  mentionRequest = null,
  // The creator scope this composer asks under, owned by the parent so it can
  // be persisted. Mentions in the text add to it as they're typed.
  creatorIds = [],
  onCreatorIdsChange,
  variant = "default",
}) {
  const [value, setValue] = useState("");
  const [token, setToken] = useState(null);
  const [remoteCreators, setRemoteCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef(null);
  // The value we believe the textarea holds. React commits a frame behind the
  // DOM, and the caret reads below have to ignore that in-between state.
  const valueRef = useRef("");
  const requestIdRef = useRef(0);
  const lastMentionNonceRef = useRef(mentionRequest?.nonce ?? 0);
  const isHero = variant === "hero";
  const sendIconSize = isHero ? 16 : 14;

  const query = token?.query ?? "";

  const suggestions = useMemo(() => {
    if (!token) return [];
    const needle = query.toLowerCase();
    const local = knownCreators
      .map(normalizeCreator)
      .filter(Boolean)
      .filter(
        (creator) =>
          !needle ||
          creator.handle.includes(needle) ||
          creator.code.toLowerCase().includes(needle) ||
          creator.name.toLowerCase().includes(needle),
      );

    return dedupeByHandle([...local, ...remoteCreators]).slice(
      0,
      MAX_SUGGESTIONS,
    );
  }, [knownCreators, query, remoteCreators, token]);

  const menuOpen =
    Boolean(token) && (suggestions.length > 0 || loadingCreators);

  const changeScope = useCallback(
    (next) => {
      if (typeof onCreatorIdsChange === "function") onCreatorIdsChange(next);
    },
    [onCreatorIdsChange],
  );

  const addCreators = useCallback(
    (handles) => {
      const known = mergeCreatorIds(
        creatorIds,
        knownCreators.map((creator) => creator?.handle || creator?.code),
      );
      const next = mergeCreatorIds(
        creatorIds,
        handles.flatMap((handle) => creatorIdsForHandle(handle, known)),
      );
      if (next.length !== creatorIds.length) changeScope(next);
      return next;
    },
    [changeScope, creatorIds, knownCreators],
  );

  /**
   * Turn the `@handles` in `text` into chips and hand back the text without
   * them, alongside the scope they make. The creator is named once, by the
   * chip, so the question itself reads as prose.
   */
  const commitMentions = useCallback(
    (text) => {
      const handles = extractMentionHandles(text);
      if (handles.length === 0) return { scope: creatorIds, text };
      return { scope: addCreators(handles), text: stripMentions(text) };
    },
    [addCreators, creatorIds],
  );

  // A handle counts once it is finished, which the text says more reliably
  // than the menu does: whatever is still being typed sits at the very end
  // with nothing after it, so it waits there until a space closes it.
  useEffect(() => {
    const trailing = value.match(/@[A-Za-z0-9_.]*$/)?.[0] ?? "";
    const settled = trailing ? value.slice(0, -trailing.length) : value;
    const { text } = commitMentions(settled);
    if (text === settled) return;

    const spacer = text && trailing && !/\s$/.test(text) ? " " : "";
    const next = `${text}${spacer}${trailing}`;
    valueRef.current = next;
    setValue(next);
  }, [commitMentions, value]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!focusSignal || !textareaRef.current) return;
    textareaRef.current.focus();
  }, [focusSignal]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [value]);

  useEffect(() => {
    setActiveIndex(0);
  }, [token]);

  // Directory lookup for the token being typed. The upstream list is the
  // authority on who exists; local matches show instantly while it lands.
  useEffect(() => {
    if (!token) {
      setRemoteCreators([]);
      setLoadingCreators(false);
      return;
    }

    const id = ++requestIdRef.current;
    setLoadingCreators(true);

    const timer = setTimeout(async () => {
      try {
        const data = await searchCreators(query);
        if (id !== requestIdRef.current) return;

        const list = Array.isArray(data?.creators)
          ? data.creators.map(normalizeCreator).filter(Boolean)
          : [];
        setRemoteCreators(list);
      } catch {
        if (id !== requestIdRef.current) return;
        setRemoteCreators([]);
      } finally {
        if (id === requestIdRef.current) setLoadingCreators(false);
      }
    }, 160);

    return () => clearTimeout(timer);
  }, [query, token]);

  // Keeps the token object stable while it means the same thing, so a keyup
  // between arrow presses doesn't reset the highlighted suggestion.
  const syncToken = useCallback(() => {
    const el = textareaRef.current;
    // A keyup landing between an accepted mention and its render would read
    // the pre-insert text and reopen the menu on a token that's already gone.
    if (!el || el.value !== valueRef.current) return;

    const next = readMentionToken(el);
    setToken((current) => {
      if (!current || !next) return current === next ? current : next;
      const same =
        current.query === next.query &&
        current.start === next.start &&
        current.end === next.end;
      return same ? current : next;
    });
  }, []);

  const closeMenu = useCallback(() => {
    requestIdRef.current += 1;
    setToken(null);
    setLoadingCreators(false);
  }, []);

  /**
   * Take a creator named from the menu, or from a click elsewhere in the app.
   * The half-typed `@handle` leaves the text with them: they are the chip now.
   */
  const acceptMention = useCallback(
    (handle, range = null) => {
      const el = textareaRef.current;
      if (!handle) return;

      if (el && range) {
        const current = valueRef.current;
        const head = current.slice(0, range.start).replace(/[ \t]+$/, "");
        const tail = current.slice(range.end).replace(/^[ \t]+/, "");
        const spacer = head && tail ? " " : "";
        const caret = head.length + spacer.length;

        valueRef.current = `${head}${spacer}${tail}`;
        setValue(valueRef.current);

        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(caret, caret);
        });
      }

      setToken(null);
      setLoadingCreators(false);
      addCreators([handle]);
    },
    [addCreators],
  );

  useEffect(() => {
    const nonce = mentionRequest?.nonce;
    if (!nonce || nonce === lastMentionNonceRef.current) return;
    lastMentionNonceRef.current = nonce;
    acceptMention(mentionRequest.handle);
  }, [acceptMention, mentionRequest]);

  const submit = () => {
    // The last word may be a mention that never got its trailing space; on
    // send it counts, and the turn goes out under the scope it makes.
    if (disabled) return;
    const committed = commitMentions(value.trim());
    const scope = committed.scope;
    const text = committed.text.trim();
    valueRef.current = "";
    setValue("");
    closeMenu();
    // Enter on a bare `@handle` names the creator and asks nothing yet.
    if (text && onSubmit) onSubmit(text, scope);
  };

  const onKeyDown = (e) => {
    if (menuOpen && suggestions.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActiveIndex(
          (current) =>
            (current + step + suggestions.length) % suggestions.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const creator = suggestions[activeIndex] || suggestions[0];
        acceptMention(creator.handle, token);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={
        "relative w-full border border-ink-200 bg-surface shadow-soft transition-colors focus-within:border-ink-300 " +
        (isHero
          ? "rounded-[1.75rem] px-5 py-3 sm:px-5 sm:py-3.5"
          : "rounded-2xl px-4 py-3")
      }
    >
      {menuOpen ? (
        <div className="absolute bottom-full left-0 z-30 mb-2 w-[min(22rem,100%)] overflow-hidden rounded-2xl border border-ink-200 bg-surface shadow-soft">
          <div className="border-b border-ink-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-400">
            Mention a creator
          </div>
          {suggestions.length === 0 ? (
            <div className="px-3 py-3 text-[13px] text-ink-400">Searching…</div>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {suggestions.map((creator, index) => (
                <li key={creator.handle}>
                  <button
                    type="button"
                    // Pointer-down so the pick lands before the textarea blurs.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      acceptMention(creator.handle, token);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors " +
                      (index === activeIndex ? "bg-ink-100" : "hover:bg-ink-50")
                    }
                  >
                    <CreatorAvatar
                      name={creator.name}
                      seed={creator.code || creator.handle}
                      logo={creator.logo}
                      size={26}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-ink-900">
                        {creator.name === creator.handle
                          ? `@${creator.handle}`
                          : creator.name}
                      </span>
                      {creator.name === creator.handle ? null : (
                        <span className="block truncate text-[12px] text-ink-500">
                          @{creator.handle}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <div className={isHero ? "flex items-end gap-3" : ""}>
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            valueRef.current = e.target.value;
            setValue(e.target.value);
            syncToken();
          }}
          onKeyUp={syncToken}
          onClick={syncToken}
          onBlur={closeMenu}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={
            "block w-full resize-none border-0 bg-transparent text-ink-900 focus:outline-none " +
            (isHero
              ? "min-h-[28px] min-w-0 flex-1 py-1.5 text-[16px] leading-6 placeholder:text-ink-400 md:text-[15px]"
              : "text-[15px] leading-6 placeholder:text-ink-400")
          }
        />

        <div
          className={isHero ? "shrink-0" : "mt-2 flex items-center justify-end"}
        >
          <button
            type="button"
            onClick={submit}
            disabled={disabled || !value.trim()}
            aria-label="Send"
            className={
              "flex items-center gap-1.5 rounded-full font-medium transition-colors disabled:opacity-100 " +
              (isHero
                ? "h-10 bg-ink-100 px-4 text-[14px] text-ink-500 hover:bg-ink-200 disabled:bg-ink-100 disabled:text-ink-400 md:h-9 md:text-[13px]"
                : "h-9 bg-btn px-4 text-[13px] text-btn-fg transition-opacity hover:opacity-90 disabled:bg-ink-200 disabled:text-ink-400")
            }
          >
            Send
            <svg
              width={sendIconSize}
              height={sendIconSize}
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
